import { ActionIcon, Flex, Text, Tooltip, TextInput, Select, Group, Stack } from '@mantine/core';
import {
  IconCircleCheck,
  IconEye,
  IconLink,
  IconProgress,
  IconSearch,
  IconFilter
} from '@tabler/icons-react';
import Badge from 'components/Badge';
import DataTables from 'components/DataTables';
import React, { useState, useMemo } from 'react';

interface TableAssessmentParticipantsProps {
  data: any[];
  handleViewResponse: (_item: any) => void;
  hasSelfAssessment?: boolean;
}

const TableAssessmentParticipants: React.FC<TableAssessmentParticipantsProps> = ({
  data,
  handleViewResponse
  // hasSelfAssessment = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter and search logic
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const name = (item?.name || item?.binusianID || '').toLowerCase();
        const email = (item?.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const isCompleted = item?.isAnswer === true;
        return statusFilter === 'completed' ? isCompleted : !isCompleted;
      });
    }

    return filtered;
  }, [data, searchQuery, statusFilter]);

  const columns = [
    {
      heading: 'Name',
      props: (row: { name?: string; binusianID?: string }) => (
        <Text size='sm' fw={600}>
          {row?.name || row?.binusianID || '-'}
        </Text>
      ),
      sortable: true,
      active: true,
      disabled: false,
      id: 'name'
    },
    {
      heading: 'Email',
      props: 'email',
      sortable: true,
      active: true,
      disabled: false,
      id: 'email'
    },
    {
      heading: 'Role',
      props: () => (
        <Badge styleVariant='subtle' bg='#1BA42F'>
          Participant
        </Badge>
      ),
      sortable: true,
      active: true,
      disabled: false,
      id: 'status'
    },
    {
      heading: 'Response Status',
      props: (row: any) => {
        const isCompleted = row?.isAnswer === true;

        return (
          <Tooltip label={isCompleted ? 'Completed' : 'In Progress'}>
            <Text component='span' c={isCompleted ? 'green.5' : 'yellow'}>
              {isCompleted ? <IconCircleCheck /> : <IconProgress />}
            </Text>
          </Tooltip>
        );
      },
      active: true,
      disabled: false
    },
    {
      heading: '',
      props: (row: any) => (
        <Flex gap={10}>
          {/* {hasSelfAssessment && ( */}
          <Tooltip label='Link to Assessment'>
            <ActionIcon
              variant='transparent'
              c={'grey.5'}
              onClick={() => window.open(`${row?.link}`, '_blank')}
            >
              <IconLink />
            </ActionIcon>
          </Tooltip>
          {/* )} */}
          <Tooltip label='View Responses'>
            <ActionIcon variant='transparent' c={'grey.5'} onClick={() => handleViewResponse(row)}>
              <IconEye />
            </ActionIcon>
          </Tooltip>
        </Flex>
      ),
      active: true,
      disabled: false,
      id: 'actions'
    }
  ];

  return (
    <Stack gap='md'>
      {/* Search and Filter Controls */}
      <Group justify='space-between' gap='sm' wrap='wrap' align='flex-end'>
        <TextInput
          placeholder='Search by name or email...'
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          style={{ flex: '1 1 260px', minWidth: 200 }}
        />
        <Select
          placeholder='Filter by status'
          value={statusFilter}
          onChange={(value) => setStatusFilter(value || 'all')}
          data={[
            { value: 'all', label: 'All Status' },
            { value: 'completed', label: 'Completed' },
            { value: 'in-progress', label: 'In Progress' }
          ]}
          leftSection={<IconFilter size={16} />}
          style={{ flex: '0 1 200px', minWidth: 150, maxWidth: 220 }}
        />
      </Group>

      {/* Results summary */}
      <Text size='sm' c='dimmed'>
        Showing {filteredData.length} of {data.length} participants
      </Text>

      <DataTables columns={columns} data={filteredData} withBulkData={true} />
    </Stack>
  );
};

export default TableAssessmentParticipants;
